import { z } from 'zod';

type Scalar = string | number | boolean;
export const isScalar = (v: unknown): v is Scalar =>
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

function unwrap(schema: z.ZodTypeAny): z.ZodTypeAny {
    while (true) {
        const t = schema._def?.typeName;
        if (t === z.ZodFirstPartyTypeKind.ZodEffects) {
            schema = schema._def.schema;
        } else if (t === z.ZodFirstPartyTypeKind.ZodOptional) {
            schema = schema._def.innerType;
        } else if (t === z.ZodFirstPartyTypeKind.ZodNullable) {
            schema = schema._def.innerType;
        } else if (t === z.ZodFirstPartyTypeKind.ZodDefault) {
            schema = schema._def.innerType;
        } else {
            break;
        }
    }
    return schema;
}

export function objectKeys(schema: z.ZodTypeAny): Set<string> {
    schema = unwrap(schema);
    const t = schema._def?.typeName;

    if (t === z.ZodFirstPartyTypeKind.ZodObject) {
        const shape = typeof schema._def.shape === 'function' ? schema._def.shape() : schema._def.shape;
        return new Set(Object.keys(shape));
    }

    if (t === z.ZodFirstPartyTypeKind.ZodIntersection) {
        const left = objectKeys(schema._def.left);
        const right = objectKeys(schema._def.right);
        return new Set([ ...left, ...right ]);
    }

    return new Set();
}

export function pickDiscriminatedBranch(schema: z.ZodTypeAny, discriminator: string, value: unknown): z.ZodTypeAny {
    const s = unwrap(schema);
    if (s._def?.typeName !== z.ZodFirstPartyTypeKind.ZodDiscriminatedUnion) {
        throw new Error('Schema is not a z.discriminatedUnion(...)');
    }

    // Use the value’s discriminator to select the branch
    const discValue = (value as any)?.[discriminator];
    const branch = s._def.optionsMap.get(discValue);

    if (!branch) {
        throw new Error(
            `No discriminated-union branch matches ${discriminator}=${String(discValue)}`
        );
    }

    return branch;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * Returns the set of object keys that are relevant for the given parsed value.
 *
 * Supports:
 * - `z.object(...)` (returns shape keys)
 * - `z.union([...])` (picks the first option that successfully parses the value)
 * - `z.discriminatedUnion(discriminator, [...])` (picks option by discriminator value)
 * - `z.intersection(a, b)` / `.and(...)` (unions keys from both sides, respecting unions inside)
 */
export function objectKeysForValue(schema: z.ZodTypeAny, args: {
    discriminator?: string;
    value: unknown;
}): Set<string> {
    const { discriminator, value } = args;
    const s = unwrap(schema);
    const t = s._def?.typeName;

    if (!isRecord(value)) {
        return new Set();
    }

    if (t === z.ZodFirstPartyTypeKind.ZodObject) {
        const shape = typeof s._def.shape === 'function' ? s._def.shape() : s._def.shape;
        return new Set(Object.keys(shape));
    }

    if (t === z.ZodFirstPartyTypeKind.ZodIntersection) {
        const left = objectKeysForValue(s._def.left, { discriminator, value });
        const right = objectKeysForValue(s._def.right, { discriminator, value });
        return new Set([ ...left, ...right ]);
    }

    if (t === z.ZodFirstPartyTypeKind.ZodDiscriminatedUnion) {
        if (!discriminator) {
            throw new Error('discriminator is required for z.discriminatedUnion(...) schemas');
        }
        const discValue = (value as any)?.[discriminator];
        const branch = s._def.optionsMap.get(discValue);
        if (!branch) {
            return new Set();
        }
        return objectKeysForValue(branch, { discriminator, value });
    }

    if (t === z.ZodFirstPartyTypeKind.ZodUnion) {
        for (const option of s._def.options as z.ZodTypeAny[]) {
            const res = option.safeParse(value);
            if (res.success) {
                return objectKeysForValue(option, { discriminator, value });
            }
        }
        return new Set();
    }

    return new Set();
}
